import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK, doToast } from 'store/actions';
import {
  doSetupApiCreateUser,
  doSetupApiDeleteUser,
  doSetupApiUpdateUser,
} from 'store/remote/actions';
import * as Clipboard from 'expo-clipboard';
import { TextField } from 'components/Field/TextField';
import { str } from 'lib/str';
import { Button, ButtonTheme } from 'components/Button';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { formatDate, numberToDate } from 'lib/riskbase';
import { UI_DATE_FORMAT_LONG, UI_TIME_FORMAT } from 'lib/constants';
import { theme } from 'lib/theme';
import { useConfirm } from 'hooks/useConfirm';

export const ApiUserModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsApiUser>
> = ({ route }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const { type, data } = route.params;

  const [tokenHidden, setTokenHidden] = React.useState(true);
  const tokenCopiedRef = React.useRef(false);
  const nameRef = React.useRef(type === 'existing' ? data.name : '');
  const getCanSubmit = () => !!nameRef.current.trim();
  const [canSubmit, setCanSubmit] = React.useState(getCanSubmit());
  const updateCanSubmit = () => setCanSubmit(getCanSubmit());

  const handleCopyBearerToken = async () => {
    if (type === 'new') {
      tokenCopiedRef.current = true;
      await Clipboard.setStringAsync(data.bearerToken);
      dispatch(doToast('Token Copied'));
    }
  };

  const handleChangeName = (newName: string) => {
    nameRef.current = newName;
    updateCanSubmit();
  };

  const handleSave = () => {
    if (type === 'existing') {
      dispatch(
        doSetupApiUpdateUser({
          id: data.id,
          organisationId: data.organisationId,
          name: nameRef.current,
        }),
      );
      return;
    }
    if (!tokenCopiedRef.current) {
      confirm({
        title: 'Did you copy the token?',
        text: `Make sure you${str.apos}ve copied the token as RiskBase will never show it again after pressing save`,
        cancelText: 'Cancel',
        confirmText: 'Save',
        onConfirm: () => {
          dispatch(
            doSetupApiCreateUser({
              organisationId: data.organisationId,
              name: nameRef.current,
              token: data.token,
            }),
          );
        },
      });
      return;
    }
    dispatch(
      doSetupApiCreateUser({
        organisationId: data.organisationId,
        name: nameRef.current,
        token: data.token,
      }),
    );
  };

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);
  useEsc(handleClose);

  const tokenText =
    'Tokens function like passwords so keep them safe! Anyone with this' +
    `user${str.apos}s token can access your organisation${str.apos}s data` +
    'using the API. If a token becomes compromised, delete it to revoke' +
    `that token${str.apos}s access.`;

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={type === 'existing' ? 'API User' : 'Create API User'}
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          onPressSaveButton={canSubmit ? handleSave : null}
        />
      }
    >
      <Card explanation="The name helps you identify the user. It may also be shown within RiskBase where history is shown">
        <TextField
          onChange={handleChangeName}
          placeholder="Name*"
          value={nameRef.current}
          autoFocus
          autoFocusDelay
          onEsc={handleClose}
        />
      </Card>

      {type === 'existing' && (
        <Card
          label="Token"
          explanation={
            'Use your token to authenticate with the API. ' +
            'Your token is only shown when creating the user. ' +
            `If you${str.apos}ve lost your token, delete this user and create a new one.\n\n${tokenText}`
          }
        >
          <CardRow value="Token hidden..." />
        </Card>
      )}

      {type === 'new' && (
        <Card
          label="Token"
          labelLinkText={tokenHidden ? 'Show Token' : 'Hide Token'}
          onLabelLinkPress={() => setTokenHidden(!tokenHidden)}
          explanation={
            'Use this token to authenticate with the API. ' +
            `Keep the token safe because we${str.apos}ll never show it to you again.\n\n${tokenText}`
          }
        >
          <CardRow
            value={
              tokenHidden ? 'Token hidden. Press to copy...' : data.bearerToken
            }
            onPress={handleCopyBearerToken}
            icon={ICONS.CopyLight}
          />
        </Card>
      )}

      {type === 'existing' && (
        <Card marginBottomHalf>
          <CardRow
            label="Last Accessed"
            value={
              data.lastAccessedTs
                ? `${formatDate(
                    numberToDate(data.lastAccessedTs),
                    UI_DATE_FORMAT_LONG,
                  )} ${formatDate(
                    numberToDate(data.lastAccessedTs),
                    UI_TIME_FORMAT,
                  )}`
                : 'Never'
            }
          />
        </Card>
      )}

      {type === 'existing' && (
        <Card>
          <CardRow
            label="Created"
            value={`${formatDate(
              numberToDate(data.createdTs),
              UI_DATE_FORMAT_LONG,
            )} ${formatDate(numberToDate(data.createdTs), UI_TIME_FORMAT)}`}
          />
          {data.expiredTs ? (
            <CardRow
              label="Expired"
              labelColor={theme.colors.red}
              valueColor={theme.colors.red}
              value={`${formatDate(
                numberToDate(data.expiredTs),
                UI_DATE_FORMAT_LONG,
              )} ${formatDate(numberToDate(data.expiredTs), UI_TIME_FORMAT)}`}
            />
          ) : (
            <CardRow
              label="Expires"
              value={`${formatDate(
                numberToDate(data.expireTs),
                UI_DATE_FORMAT_LONG,
              )} ${formatDate(numberToDate(data.expireTs), UI_TIME_FORMAT)}`}
            />
          )}
        </Card>
      )}

      {type === 'existing' && (
        <>
          <SeparatorHorizontal withMarginBottom />
          <Button
            text="Delete User"
            destructive
            buttonTheme={ButtonTheme.themeless}
            onPress={() =>
              dispatch(
                doSetupApiDeleteUser({
                  id: data.id,
                  organisationId: data.organisationId,
                  name: data.name,
                }),
              )
            }
            style={{ alignSelf: 'center' }}
          />
        </>
      )}
    </PageScrollView>
  );
};
